/**
 * WebSmDoors — the "one door" choice over the vault artwork (ported from
 * Glow's WebSmDoors_type.jsx). Left half → CLICK1, right half → CLICK2.
 * Choosing plays the unlock: the key from the opening screen flies into the
 * door's keyhole and turns, the door swings open, and the view pushes through
 * the doorway before the flow moves on.
 * Options: BackgroundImage / LeftLabel / RightLabel / LeftColor / RightColor
 *   / Title / BgColor
 */
import { useEffect, useState, type CSSProperties } from "react";
import { ChevronUp } from "lucide-react";
import { useMissionFile } from "@/features/funnel/api/use-mission-file";
import { filedCount } from "../dev-mission";
import { opLink } from "@/features/funnel/hooks/use-phone-login";
import { useDailyDoors } from "@/features/funnel/api/use-doors";
import type { StepTypeProps } from "./types";
import hallImg from "./sm-doors-hall.webp";
import hardwareImg from "./sm-doors-hardware.webp";
import defaultDoor from "./door-default.webp";
import keyImg from "./key-front.webp";
import { COMEBACK_KEY } from "../comeback-inbox";
import { engravedName } from "./operative-name";

/** A link from a later text: how many days they were away (read once). */
const takeComeback = () => {
  try {
    const days = Number(sessionStorage.getItem(COMEBACK_KEY));
    sessionStorage.removeItem(COMEBACK_KEY);
    return days > 0 ? days : 0;
  } catch {
    return 0;
  }
};
/** How long the welcome stays up. */
const WELCOME_MS = 6000;

/** How long they can stand in the hall before we point at the doors. */
const HINT_MS = 7000;

// The door titles sit in the stone wall above each door, between the crossbeam
// and the door frame, centred on the door below and as wide as it. Their
// vertical centre as a fraction of the image height. Re-measure (with
// scripts/build-doors-art.py's HOLES) if the art changes.
const TITLE_Y = "23.3%";
// (the screens there are 9.6% of the image tall — crossbeam to door frame —
// and grow for a title too long to fit)

/**
 * Each door's opening in the art, as fractions of the image (x, y, w, h) — the
 * same boxes scripts/build-doors-art.py fits the doors into. `hinge` is the
 * side away from the handle, which the door swings on when it opens.
 */
const DOOR_OPENING = {
  left: {
    x: 46 / 937,
    y: 512 / 1678,
    w: 344 / 937,
    h: 689 / 1678,
    hinge: "left",
  },
  right: {
    x: 542 / 937,
    y: 512 / 1678,
    w: 347 / 937,
    h: 689 / 1678,
    hinge: "right",
  },
} as const;
/**
 * Each door's keyhole centre as fractions of the image — drawn into the art by
 * scripts/build-doors-art.py (keyhole()), just under the pull.
 */
const KEYHOLE = {
  left: { x: 359 / 937, y: 962 / 1678 },
  right: { x: 573 / 937, y: 962 / 1678 },
} as const;
/** key-front.webp: its tip's x as a fraction of its width, and its aspect. */
const KEY_TIP_X = 0.505;
const KEY_ASPECT = 529 / 1645;

// The unlock, in order: the key flies in, pushes in and turns (KEY_MS), pulls
// out and floats up toward the viewer, big, mid-screen (LIFT_MS), holds, then
// crumbles to dust (DUST_MS) while the door swings open (from DOOR_AT, for
// OPEN_MS), then the view walks through it (WALK_MS).
const KEY_MS = 1800;
const LIFT_MS = 800;
const DUST_AT = KEY_MS + LIFT_MS + 200;
const DUST_MS = 2600;
const DOOR_AT = DUST_AT + 1600;
/** How big the key gets when it comes up to the viewer. */
const LIFT_SCALE = 3.4;
/** The key's rendered height in the image box, before any scaling. */
const KEY_H = 9;

/**
 * The key's whole path as one keyframe list, so each stage's share of the
 * timeline follows the constants above. It hangs off its tip (the anchor), so
 * the lifted key's tip sits half its height below the screen centre.
 */
const keyPath = (hole: { x: number; y: number }, lift: boolean) => {
  const T = lift ? KEY_MS + LIFT_MS : KEY_MS;
  const at = (ms: number) => `${((ms / T) * 100).toFixed(1)}%`;
  const lx = `${(0.5 - hole.x) * 100}cqw`;
  const ly = `${(0.5 - hole.y) * 100 + (KEY_H * LIFT_SCALE) / 2}cqh`;
  const f = (x: string, y: string, r: number, sx: number, sy: number) =>
    `transform:translate(${x},${y}) rotate(${r}deg) scale(${sx},${sy})`;
  return `@keyframes smKeyPath{
    0%{opacity:0;${f("0px", "-60cqh", 0, 2.4, 2.4)}}
    ${at(KEY_MS * 0.55)}{opacity:1;${f("0px", "0px", 0, 1, 1)}}
    ${at(KEY_MS * 0.72)}{${f("0px", "0px", 0, 1, 0.42)}}
    ${at(KEY_MS)}{${f("0px", "0px", 90, 1, 0.42)}}
    ${
      lift
        ? `${at(KEY_MS + 150)}{${f("0px", "0px", 90, 1, 1)}}
    100%{opacity:1;${f(lx, ly, 0, LIFT_SCALE, LIFT_SCALE)}}`
        : ""
    }
  }`;
};

/**
 * The key comes up close and crumbles the first time only — that is the
 * moment it is spent on their first door. After that it is simply theirs: it
 * turns in the lock, then flies off screen as the door swings open, and the
 * door opens sooner. First mission only (nothing filed yet), and remembered per
 * browser so a replay of that first run does not crumble it again.
 */
const CRUMBLED_KEY = "secret-missions-key-crumbled";
const hasCrumbled = () => {
  try {
    return localStorage.getItem(CRUMBLED_KEY) === "1";
  } catch {
    return false;
  }
};

const OPEN_MS = 850;
const WALK_MS = 900;

// The key crumbles as a grid of pieces, each drifting off its own way. The
// scatter is a fixed hash, not Math.random, so it renders the same every time.
const DUST_COLS = 5;
const DUST_ROWS = 14;
const hash = (n: number) => {
  const v = Math.sin(n * 12.9898) * 43758.5453;
  return v - Math.floor(v);
};
const DUST = Array.from({ length: DUST_COLS * DUST_ROWS }, (_, i) => {
  const c = i % DUST_COLS;
  const r = Math.floor(i / DUST_COLS);
  const w = 100 / DUST_COLS;
  const h = 100 / DUST_ROWS;
  return {
    clip: `inset(${r * h}% ${(DUST_COLS - c - 1) * w}% ${(DUST_ROWS - r - 1) * h}% ${c * w}%)`,
    // the bow (top rows) goes first, crumbling down toward the lock
    delay: DUST_AT + r * 90 + hash(i) * 400,
    vars: {
      "--sm-dx": `${(hash(i + 7) - 0.5) * 10}cqh`,
      "--sm-dy": `${-2 - hash(i + 13) * 7}cqh`,
      "--sm-rot": `${(hash(i + 29) - 0.5) * 220}deg`,
    },
  };
});

/** The artwork's rendered width: it always fills the viewport height. */
const IMG_W = "calc(100dvh * 937 / 1678)";
/** Breathing room between the label and the screen edge. */
const PAD = "10px";
/** The player frame's width (see routes/index.tsx: capped by aspect). */
const FRAME_W = "min(100vw, 100dvh * 0.62)";

/** An image-fraction x as a viewport position (the art is centered). */
const atX = (frac: number) => `calc(50% + ${frac - 0.5} * ${IMG_W})`;

// On a phone the art is wider than the screen, so a label centred on its door
// runs off the edge. Each label spans the part of its door that is actually visible, and is
// centred on the door where that fits, otherwise nudged inward.
/** Each sign's nudge off its door's centre, as a fraction of the image. */
const SIGN_NUDGE = { left: 0, right: 18 / 937 };
const labelBox = (side: "left" | "right") => {
  const d = { ...DOOR_OPENING[side] };
  d.x += SIGN_NUDGE[side];
  // The label lives in the art (so it rides the walk-through zoom), and the
  // art is centred on the screen, so the screen's edges are 50% ± half of it.
  const lo = `max(50% - ${FRAME_W} / 2 + ${PAD}, ${atX(d.x)})`;
  const hi = `min(50% + ${FRAME_W} / 2 - ${PAD}, ${atX(d.x + d.w)})`;
  const width = `min(${IMG_W} * ${d.w}, ${hi} - ${lo})`;
  const center = atX(d.x + d.w / 2);
  return {
    width,
    left: `calc(clamp(${lo} + ${width} / 2, ${center}, ${hi} - ${width} / 2))`,
  };
};

/** A door's opening as a box in the art. */
const doorBox = (d: { x: number; y: number; w: number; h: number }) => ({
  left: `${d.x * 100}%`,
  top: `${d.y * 100}%`,
  width: `${d.w * 100}%`,
  height: `${d.h * 100}%`,
});

/** A mission's door art, trimmed a little each side and stretched into the
 *  opening (the same fit scripts/build-doors-art.py used when it was baked). */
const FACE_TRIM = 0.03;
const FACE_SIZE = `${100 / (1 - 2 * FACE_TRIM)}% 100%`;

/** Zoom on a door's centre while sliding that centre to the screen's. */
const walkVars = (d: { x: number; y: number; w: number; h: number }) => {
  const cx = d.x + d.w / 2;
  const cy = d.y + d.h / 2;
  return {
    transformOrigin: `${cx * 100}% ${cy * 100}%`,
    "--sm-walk-x": `${-cx * 100}%`,
    "--sm-walk-y": `${-cy * 100}%`,
  };
};

const Label = ({
  side,
  text,
  color,
  ready,
}: {
  side: "left" | "right";
  text: string;
  color: string;
  ready: boolean;
}) => {
  if (!text) return null;
  // Each door's label colour is authored, and it drives both the text and its
  // glow, so it rides along as a custom property. It sits in the wall panel
  // above its door (labelBox / TITLE_Y) — the door art is busy enough that
  // text laid over the door itself does not read — on a display screen set
  // into the stone there: part of the picture from the first frame (dark
  // glass), powering on in the door's colour when the doors are ready.
  // Longer titles set smaller (by area: size falls with √length) so they
  // still fit the screen.
  const fontCqw = Math.min(3.8, 3.8 * Math.sqrt(22 / text.length));
  const vars = {
    "--sm-door": color,
    "--sm-title-y": TITLE_Y,
    ...labelBox(side),
    fontSize: `clamp(9.5px, ${fontCqw.toFixed(2)}cqw, 20px)`,
  } as CSSProperties;
  return (
    <div
      style={vars}
      className="pointer-events-none absolute top-[var(--sm-title-y)] z-[2] flex min-h-[9.6cqh] -translate-x-1/2 -translate-y-1/2"
    >
      {/* once lit, its light spills on the stone around it */}
      <div
        className={`absolute -inset-[3cqw] rounded-[50%] bg-[radial-gradient(closest-side,color-mix(in_srgb,var(--sm-door)_22%,transparent),transparent)] blur-[1.2cqw] transition-opacity duration-700 ${
          ready ? "opacity-100" : "opacity-0"
        }`}
      />
      {/* the bars it hangs from, up to the crossbeam */}
      <div className="absolute bottom-full left-[18%] h-[3cqh] w-[1.1cqw] bg-[linear-gradient(90deg,#141312,#4a4642_45%,#141312)] shadow-[0_0_4px_rgba(0,0,0,.8)]" />
      <div className="absolute right-[18%] bottom-full h-[3cqh] w-[1.1cqw] bg-[linear-gradient(90deg,#141312,#4a4642_45%,#141312)] shadow-[0_0_4px_rgba(0,0,0,.8)]" />
      {/* set into the wall: a recess cut in the stone (shadowed at the top,
          catching the light on its lower lip), a dull gunmetal frame, glass */}
      <div className="relative flex flex-1 rounded-[.8cqw] bg-[#0a0a0b] p-[.8cqw] shadow-[inset_0_.8cqw_1.4cqw_rgba(0,0,0,.95),0_1px_0_rgba(255,236,200,.14),0_-1px_0_rgba(0,0,0,.6)]">
        <div className="relative flex flex-1 rounded-[.6cqw] bg-[linear-gradient(180deg,#2c2b2a,#1b1a1a_50%,#262422)] p-[.7cqw] shadow-[inset_0_1px_0_rgba(255,240,220,.12),0_1px_2px_rgba(0,0,0,.8)]">
          <div
            className={`relative flex flex-1 items-center justify-center overflow-hidden rounded-[.4cqw] bg-[#020304] px-[2cqw] py-[1.2cqw] text-center font-sans leading-[1.2] font-extrabold tracking-[.04em] text-balance uppercase shadow-[inset_0_0_1.6cqw_rgba(0,0,0,.9)] ${
              ready ? "" : "text-transparent"
            }`}
          >
            {/* the lit panel: dark glass until the doors are ready, then it
                powers on in the door's colour */}
            <div
              className={`absolute inset-0 bg-[radial-gradient(ellipse_at_50%_45%,color-mix(in_srgb,var(--sm-door)_18%,#04070a),#020304_85%)] ${
                ready ? "animate-[smScreenOn_.7s_ease-out_both]" : "opacity-0"
              }`}
            />
            <span
              className={`relative text-[color-mix(in_srgb,var(--sm-door)_70%,white)] [text-shadow:0_0_.6cqw_var(--sm-door),0_0_2cqw_color-mix(in_srgb,var(--sm-door)_55%,transparent)] ${
                ready ? "animate-[smScreenOn_.7s_ease-out_both]" : "opacity-0"
              }`}
            >
              {text}
            </span>
            {/* the panel's pixel grid, curved-glass shading, and a reflection */}
            <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,rgba(0,0,0,.18)_0_1px,transparent_1px_3px),repeating-linear-gradient(0deg,rgba(0,0,0,.12)_0_1px,transparent_1px_3px)]" />
            <div className="absolute inset-0 shadow-[inset_0_0_2.2cqw_rgba(0,0,0,.85)]" />
            <div className="absolute inset-x-0 top-0 h-[40%] bg-[linear-gradient(170deg,rgba(255,255,255,.06),transparent_60%)]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default function WebSmDoorsType({ getOpt, dispatch }: StepTypeProps) {
  const bg = getOpt("BgColor") || "#000000";
  // The art is layered: the hallway (its doors plain), each mission's door
  // (missionbank.DoorImage) over its opening, then the handles and keyholes.
  // (The hallway's own plain doors show only while the doors are loading.)
  const hall = getOpt("BackgroundImage") || hallImg;
  const title = getOpt("Title") || "";

  // The door labels are the vague descriptions of today's two missions, so
  // they change daily and cannot live in the step options. The options stay
  // as the authoring override — useful for pinning a label while testing.
  const { data: doors } = useDailyDoors();
  const labelFor = (family: "love" | "stranger") =>
    doors?.doors.find((d) => d.family === family)?.label ?? "";
  const leftLabel = labelFor("love") || getOpt("LeftLabel") || "";
  const rightLabel = labelFor("stranger") || getOpt("RightLabel") || "";
  // A mission without its own door art gets the stand-in (door-default.webp,
  // scripts/build-default-door.py) — never a blank door. Nothing until the
  // doors arrive, so the stand-in does not flash before the real one.
  const faceFor = (family: "love" | "stranger") =>
    doors
      ? doors.doors.find((d) => d.family === family)?.image || defaultDoor
      : "";
  const faces = { left: faceFor("love"), right: faceFor("stranger") };
  const leftColor = getOpt("LeftColor") || "#8fbfe8";
  const rightColor = getOpt("RightColor") || "#e8b74e";

  const [ready, setReady] = useState(false);
  const [hover, setHover] = useState<"left" | "right" | null>(null);
  // The chosen door swings open, then the flow moves on. One choice only.
  const [opening, setOpening] = useState<"left" | "right" | null>(null);
  // Only on their first mission (nothing filed yet), and once per browser.
  // (not before the file has loaded: a returning operative's key never crumbles)
  const { data: filed = [], isSuccess: fileLoaded } = useMissionFile();
  const [crumbledHere] = useState(hasCrumbled);
  // A browser that does not know them yet (they sign in at the case) sees an
  // empty file; but one that came in on a link we texted (`op`) is a returning
  // operative — those texts only go out once a mission is filed.
  const crumble =
    fileLoaded && !crumbledHere && !opLink() && filedCount(filed) === 0;
  const doorAt = crumble ? DOOR_AT : KEY_MS + 300;
  const eventFor = (side: "left" | "right") =>
    side === "left" ? "CLICK1" : "CLICK2";
  const choose = (side: "left" | "right") => {
    if (opening) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      dispatch(eventFor(side));
      return;
    }
    setOpening(side);
    if (crumble) {
      try {
        localStorage.setItem(CRUMBLED_KEY, "1");
      } catch {
        /* private mode: it crumbles again next time */
      }
    }
  };
  // Through the door once the walk is done. Held by an effect so a Back press
  // mid-walk cancels it: a stray timer would dispatch this door's event into
  // whichever step is current by then.
  useEffect(() => {
    if (!opening) return;
    const t = setTimeout(
      () => dispatch(eventFor(opening)),
      doorAt + OPEN_MS + WALK_MS,
    );
    return () => clearTimeout(t);
    // one choice only: timed from the moment the door is picked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening]);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 2000);
    return () => clearTimeout(t);
  }, []);
  // back after days away, on one of our texts: no lecture, a welcome
  const [away, setAway] = useState(takeComeback);
  useEffect(() => {
    if (!away) return;
    const t = setTimeout(() => setAway(0), WELCOME_MS);
    return () => clearTimeout(t);
  }, [away]);
  const operative = engravedName();
  // still standing there: point at the doors
  const [hint, setHint] = useState(false);
  useEffect(() => {
    if (!ready) return;
    const t = setTimeout(() => setHint(true), HINT_MS);
    return () => clearTimeout(t);
  }, [ready]);
  const showHint = hint && !opening;

  const half = (side: "left" | "right", label: string, onClick: () => void) => (
    <button
      type="button"
      aria-label={label || (side === "left" ? "Left door" : "Right door")}
      data-testid={`door-${side}`}
      // (tests wait on this rather than on the zoom's timing)
      data-ready={ready || undefined}
      onMouseEnter={() => setHover(side)}
      onMouseLeave={() => setHover((h) => (h === side ? null : h))}
      onClick={ready && !opening ? onClick : undefined}
      className={`flex-1 border-0 p-0 transition-[background] duration-300 ${
        ready ? "cursor-pointer" : "cursor-default"
      } ${
        hover === side && ready
          ? "bg-[radial-gradient(circle_at_50%_42%,rgba(255,255,255,.14),transparent_62%)]"
          : "bg-transparent"
      }`}
    />
  );

  return (
    <section
      style={{ "--sm-bg": bg } as CSSProperties}
      className="relative h-[100dvh] overflow-hidden bg-[var(--sm-bg)]"
    >
      <div
        style={
          opening
            ? ({
                // walk through: push in on the open doorway, panning it to the
                // middle of the screen (on a phone its centre is off-screen)
                ...walkVars(DOOR_OPENING[opening]),
                animation: `smWalk ${WALK_MS}ms cubic-bezier(.55,0,.8,.6) ${doorAt + OPEN_MS - 150}ms both`,
              } as CSSProperties)
            : undefined
        }
        className="absolute top-1/2 left-1/2 h-full -translate-x-1/2 -translate-y-1/2 overflow-hidden aspect-[937/1678] [container-type:size]"
      >
        <div className="absolute inset-0 animate-[smDoorsZoom_1.9s_cubic-bezier(.2,.7,.25,1)_both]">
          <div
            style={{ backgroundImage: `url(${hall})` }}
            className="absolute inset-0 bg-cover bg-center"
          />
          {(["left", "right"] as const).map((side) =>
            faces[side] ? (
              <div
                key={side}
                style={{
                  ...doorBox(DOOR_OPENING[side]),
                  backgroundImage: `url(${faces[side]})`,
                  backgroundSize: FACE_SIZE,
                }}
                className="absolute bg-center"
              />
            ) : null,
          )}
          <div
            style={{ backgroundImage: `url(${hardwareImg})` }}
            className="absolute inset-0 bg-cover bg-center"
          />
          {/* built into the wall: they arrive with the picture and zoom with it */}
          <Label side="left" text={leftLabel} color={leftColor} ready={ready} />
          <Label
            side="right"
            text={rightLabel}
            color={rightColor}
            ready={ready}
          />
        </div>

        <div className="absolute inset-0 z-[3] flex">
          {half("left", leftLabel, () => choose("left"))}
          {half("right", rightLabel, () => choose("right"))}
        </div>

        {/* The chosen door, lifted out of the art as its own layer (the same
            image, sized and offset so only the door shows) and swung outward,
            toward the viewer,
            on its hinge, with light through the opening behind it. */}
        {opening
          ? (() => {
              const d = DOOR_OPENING[opening];
              const color = opening === "left" ? leftColor : rightColor;
              // the art's layers, cut to this door (see the static art above)
              const cut = `${100 / d.w}% ${100 / d.h}%`;
              const at = `${(d.x / (1 - d.w)) * 100}% ${(d.y / (1 - d.h)) * 100}%`;
              const face = faces[opening];
              return (
                <div
                  style={doorBox(d)}
                  className="absolute z-[4] [perspective:900px]"
                >
                  {/* An empty doorway behind the swinging door: solid, so the
                      painted closed door in the art never shows through while
                      the light is still coming up. */}
                  <div className="absolute inset-0 bg-[#050403]" />
                  <div
                    style={{
                      background: `radial-gradient(ellipse at 50% 55%, #fff8e8 0%, ${color} 38%, rgba(0,0,0,.85) 100%)`,
                      animation: `smDoorLight ${OPEN_MS}ms ease-out ${doorAt}ms both`,
                    }}
                    className="absolute inset-0"
                  />
                  <div
                    style={{
                      backgroundImage: [hardwareImg, face, hall]
                        .filter(Boolean)
                        .map((u) => `url(${u})`)
                        .join(", "),
                      backgroundSize: [cut, face && FACE_SIZE, cut]
                        .filter(Boolean)
                        .join(", "),
                      backgroundPosition: [at, face && "center", at]
                        .filter(Boolean)
                        .join(", "),
                      transformOrigin: d.hinge,
                      animation: `${d.hinge === "left" ? "smDoorOpenL" : "smDoorOpenR"} ${OPEN_MS}ms cubic-bezier(.45,0,.2,1) ${doorAt}ms both`,
                    }}
                    className="absolute inset-0 shadow-[0_0_30px_rgba(0,0,0,.8)]"
                  />
                </div>
              );
            })()
          : null}

        {/* The key: anchored at the keyhole by its tip (a 0x0 box the key
            hangs off), so flying in, pushing in (squashed toward the tip) and
            turning all pivot on the lock. Once it has turned it crumbles to
            dust: the key is drawn as DUST pieces that drift apart. */}
        {opening ? (
          <>
            {/* the hallway dims while the key is up close */}
            {crumble ? (
              <div
                style={{
                  animation: `smDim ${DOOR_AT - KEY_MS}ms ease-in-out ${KEY_MS}ms both`,
                }}
                className="pointer-events-none absolute inset-0 z-[5] bg-black opacity-0"
              />
            ) : null}
            <div
              style={{
                left: `${KEYHOLE[opening].x * 100}%`,
                top: `${KEYHOLE[opening].y * 100}%`,
                background: `radial-gradient(circle, #fff8e8 0%, ${opening === "left" ? leftColor : rightColor} 35%, transparent 70%)`,
                animation: `smKeyFlash 700ms ease-out ${KEY_MS - 300}ms both`,
              }}
              className="absolute z-[5] size-[9cqh] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0"
            />
            <div
              style={{
                left: `${KEYHOLE[opening].x * 100}%`,
                top: `${KEYHOLE[opening].y * 100}%`,
                animation: crumble
                  ? `smKeyPath ${KEY_MS + LIFT_MS}ms cubic-bezier(.3,.7,.3,1) both`
                  : // unlocks the door, then flies off up and away, still theirs
                    `smKeyPath ${KEY_MS}ms cubic-bezier(.3,.7,.3,1) both, smKeyFly ${OPEN_MS}ms cubic-bezier(.5,0,.8,.4) ${doorAt}ms forwards`,
              }}
              className="absolute z-[6] size-0 origin-top-left"
            >
              {(crumble ? DUST : [null]).map((p, i) => (
                <img
                  key={i}
                  src={keyImg}
                  alt=""
                  style={
                    {
                      height: `${KEY_H}cqh`,
                      left: `calc(-${KEY_H}cqh * ${KEY_ASPECT * KEY_TIP_X})`,
                      ...(p && {
                        ...p.vars,
                        clipPath: p.clip,
                        animation: `smDust ${DUST_MS}ms cubic-bezier(.2,.6,.4,1) ${p.delay}ms forwards`,
                      }),
                    } as CSSProperties
                  }
                  className="absolute bottom-0 max-w-none"
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      {/* the far side of the door: the walk-through ends in the dark */}
      {opening ? (
        <div
          style={{
            animation: `smFade ${WALK_MS * 0.5}ms ease-in ${doorAt + OPEN_MS + WALK_MS * 0.5 - 150}ms both`,
          }}
          className="pointer-events-none absolute inset-0 z-[10] bg-[var(--sm-bg)]"
        />
      ) : null}

      {/* the hint, from the bottom: an arrow under each door pointing up
          at it, and the words */}
      {showHint ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-[max(28px,6dvh)] z-[4] flex animate-[smFade_.6s_ease-out_both] flex-col items-center gap-2 font-stencil text-[13px] tracking-[3px] text-[#eadfc6] uppercase">
          <div className="flex w-full justify-around">
            {[leftColor, rightColor].map((c) => (
              <span
                key={c}
                style={{ color: c }}
                className="grid size-11 animate-[smHintBob_1.1s_ease-in-out_infinite] place-items-center rounded-full border-2 border-current bg-black/75 shadow-[0_0_18px_currentColor,0_4px_12px_rgba(0,0,0,.9)]"
              >
                <ChevronUp
                  strokeWidth={3}
                  className="size-7 [filter:drop-shadow(0_0_6px_currentColor)]"
                />
              </span>
            ))}
          </div>
          <span className="rounded-full border border-[#d6a94e]/50 bg-black/70 px-4 py-2 [text-shadow:0_0_14px_rgba(214,169,78,.5)]">
            Tap a door to choose it
          </span>
        </div>
      ) : null}

      {away && !opening ? (
        <div
          role="status"
          className="pointer-events-none absolute inset-x-4 top-[max(120px,16dvh)] z-[5] mx-auto max-w-[400px] animate-[smFade_.5s_ease-out_both] rounded-[10px] border border-[#d6a94e]/40 bg-black/80 px-4 py-3 text-center shadow-[0_14px_40px_rgba(0,0,0,.7)]"
        >
          <div className="font-mono text-[10px] tracking-[.24em] text-[#d6a94e] uppercase">
            Welcome back{operative ? `, ${operative}` : ""}
          </div>
          <div className="mt-1 text-[15px] leading-snug text-[#f4eddb]">
            {away === 1 ? "A day" : `${away} days`} away. Nothing lost: your
            locks are still open and your file is where you left it. Pick a
            door.
          </div>
        </div>
      ) : null}

      {title ? (
        // the wall up there is busy: shade it so the wording reads
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 z-[4] h-[118px] bg-[linear-gradient(to_bottom,rgba(0,0,0,.8)_0%,rgba(0,0,0,.6)_60%,transparent_100%)] transition-opacity duration-700 ${
            ready ? "opacity-100" : "opacity-0"
          }`}
        />
      ) : null}
      {title ? (
        <div
          className={`pointer-events-none absolute top-[26px] right-0 left-0 z-[4] px-5 text-center transition-opacity duration-700 ${
            ready ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="mb-[9px] font-stencil text-[10.5px] tracking-[4px] text-[#d6a94e] uppercase [text-shadow:0_0_14px_rgba(214,169,78,.5)]">
            ▸ Clearance Required ◂
          </div>
          <div className="mx-auto max-w-[460px] font-stencil text-[clamp(13px,3.7cqw,18px)] leading-[1.3] tracking-[1.2px] text-balance text-[#eadfc6] uppercase [text-shadow:0_0_18px_rgba(214,169,78,.3),0_2px_14px_rgba(0,0,0,.9)]">
            {title}
          </div>
          <div className="mx-auto mt-3 h-px w-11 bg-[linear-gradient(90deg,transparent,#d6a94e,transparent)]" />
        </div>
      ) : null}

      {/* No underscore in the name: Tailwind reads `_` in an arbitrary value
          as a space, so `sm_doorsZoom` would never match this keyframe. */}
      {opening ? <style>{keyPath(KEYHOLE[opening], crumble)}</style> : null}
      <style>{`
        @keyframes smDoorsZoom{0%{opacity:0;transform:scale(1.16)}100%{opacity:1;transform:scale(1)}}
        @keyframes smDoorOpenL{from{transform:rotateY(0)}to{transform:rotateY(-78deg)}}
        @keyframes smDoorOpenR{from{transform:rotateY(0)}to{transform:rotateY(78deg)}}
        @keyframes smDoorLight{from{opacity:.2}to{opacity:1}}
        @keyframes smDust{0%{opacity:1;filter:brightness(1)}30%{opacity:1;filter:brightness(1.8) sepia(.4)}90%{opacity:1}100%{opacity:0;translate:var(--sm-dx) var(--sm-dy);rotate:var(--sm-rot);scale:.3;filter:brightness(2)}}
        @keyframes smDim{0%{opacity:0}30%{opacity:.5}75%{opacity:.5}100%{opacity:0}}
        @keyframes smKeyFly{to{translate:45cqw -110cqh;rotate:-35deg}}
        @keyframes smKeyFlash{0%{opacity:0}35%{opacity:.9}100%{opacity:0}}
        @keyframes smWalk{from{scale:1;translate:-50% -50%}to{scale:5;translate:var(--sm-walk-x) var(--sm-walk-y)}}
        @keyframes smFade{from{opacity:0}to{opacity:1}}
        @keyframes smHintBob{0%,100%{translate:0 0}50%{translate:0 -30%}}
        @keyframes smScreenOn{0%{transform:scaleY(.04);filter:brightness(3)}35%{transform:scaleY(1);filter:brightness(1.8)}50%{opacity:.55}60%{opacity:1;filter:brightness(1)}75%{opacity:.8}100%{opacity:1;transform:none;filter:none}}
      `}</style>
    </section>
  );
}
